/* Biometric enroll tab — extracted from BiometricPunch.js (iter85).
 * Receives all state/callbacks via a single spread props bag. Every
 * `data-testid` is preserved so existing e2e specs keep working. */
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Fingerprint, ClockCounterClockwise, Trash, UserCircle, ArrowClockwise, ArrowsClockwise, CurrencyDollar, WarningCircle, DownloadSimple, ArrowSquareOut } from '@phosphor-icons/react';

export const BiometricEnrollTab = ({
  enrollId, setEnrollId, enrollKind, setEnrollKind,
  enrollLabel, setEnrollLabel,
  creds, teachers, staff, employeeOptions,
  enroll, deleteCred, busy, openPlanning, webauthnSupported,
  manualPunch, toast,
}) => (
  <>
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Type d&apos;employé</Label>
                <select value={enrollKind} onChange={(e) => { setEnrollKind(e.target.value); setEnrollId(''); }}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="enroll-kind-select">
                  <option value="teacher">Enseignant</option>
                  <option value="staff">Personnel (cantine, transport, éducateur, nettoyage, direction)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Employé</Label>
                <select value={enrollId} onChange={(e) => setEnrollId(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="enroll-employee-select">
                  <option value="">— Choisir un employé —</option>
                  {employeeOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Libellé de l&apos;empreinte</Label>
                <input value={enrollLabel} onChange={(e) => setEnrollLabel(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  placeholder="ex: index droit"
                  data-testid="enroll-label-input" />
              </div>
              <div className="md:col-span-2 flex items-end">
                <Button onClick={enroll} disabled={busy || !enrollId || !webauthnSupported}
                  className="bg-emerald-600 hover:bg-emerald-700 w-full"
                  data-testid="enroll-submit-btn">
                  <Fingerprint size={16} className="mr-2" />
                  {busy ? 'En cours…' : 'Enrôler l\'empreinte'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {enrollId && (
            <Card className="border border-[#E2E8F0] shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div>
                    <h3 className="font-outfit font-semibold text-sm">
                      Empreintes enrôlées ({creds.length})
                    </h3>
                    <p className="text-[10px] text-[#94A3B8]">
                      Chaque employé peut enrôler plusieurs empreintes (index gauche + droit, secours, etc.). Le libellé permet de les distinguer.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {creds.length > 0 && (
                      <Button size="sm" variant="outline" onClick={() => { setEnrollLabel(`Empreinte ${creds.length + 1}`); toast.info('Prêt à enrôler une empreinte supplémentaire'); }}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="add-fingerprint-btn">
                        <Fingerprint size={14} className="mr-1" /> Ajouter une empreinte
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={openPlanning}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      data-testid="edit-planning-btn">
                      📅 Planning
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => manualPunch(enrollKind, enrollId, 'in')}
                      data-testid="manual-in-btn">Manuel: Entrée</Button>
                    <Button size="sm" variant="outline" onClick={() => manualPunch(enrollKind, enrollId, 'out')}
                      data-testid="manual-out-btn">Manuel: Sortie</Button>
                  </div>
                </div>
                {creds.length === 0 ? (
                  <p className="text-sm text-[#94A3B8] italic">Aucune empreinte enrôlée pour cet employé.</p>
                ) : (
                  <div className="space-y-1.5">
                    {creds.map((c) => (
                      <div key={c.id} className="flex items-center justify-between p-2 bg-slate-50 rounded" data-testid={`cred-row-${c.id}`}>
                        <div className="flex items-center gap-2">
                          <Fingerprint size={20} className="text-emerald-600" weight="duotone" />
                          <div>
                            <div className="font-medium text-sm">{c.label}</div>
                            <div className="text-[10px] text-[#94A3B8]">
                              enrôlée {c.created_at?.slice(0, 10)} · usage {c.sign_count || 0}×
                              {c.last_used_at && ` · dernier ${new Date(c.last_used_at).toLocaleDateString('fr-FR')}`}
                            </div>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => deleteCred(c.id)}
                          className="text-red-600 hover:bg-red-50 border-red-200"
                          data-testid={`delete-cred-${c.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
  </>
);
